import type { ChooseContact } from "@appchoose/contact";
import * as Sentry from "@sentry/react";
import { useAtomValue } from "jotai/react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { cn } from "~/components/ui/cn";
import { CopyIcon } from "~/components/ui/icons/copy";
import { MailIcon } from "~/components/ui/icons/mail";
import { toast } from "~/components/ui/toast";
import { useCopyToClipboard } from "~/hooks/use-copy-to-clipboard";
import { track } from "~/libs/analytics";
import { brandState } from "~/stores/brand";

type AmContactProps = {
  className?: string;
  from: string;
  contact: ChooseContact;
  horizontal?: boolean;
};

export const AmContact: React.FC<AmContactProps> = ({
  className,
  contact,
  from,
  horizontal = false,
}) => {
  const { t } = useTranslation();
  const brand = useAtomValue(brandState);

  return (
    <div
      className={cn(
        horizontal
          ? "grid gap-6 sm:grid-cols-2 sm:items-center sm:gap-10"
          : "flex flex-col justify-center gap-6",
        className,
      )}
    >
      <div className="flex items-center gap-4">
        <AmContactImage contact={contact} />
        <div className="flex flex-col gap-[0.12rem]">
          <div className="font-bold leading-5.5 text-gray-900">
            {t("invoice.invoice_locked.am_contact.account_manager", {
              name: contact.firstName,
            })}
          </div>
          <div className="max-w-[13.875rem] text-xs leading-[1.125rem] text-gray-700">
            {t("invoice.invoice_locked.am_contact.need_help")}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="rounded bg-gray-50 p-2 text-center text-sm font-semibold text-gray-900">
          {contact.email}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <a
            href={`mailto:${contact.email}?subject=${encodeURIComponent(
              t("support_email.subject_prefill", {
                brandName: brand?.name ?? "",
              }),
            )}`}
            className={cn(
              "flex place-content-center items-center gap-1 rounded-lg border border-gray-100 bg-[#FBFBFB] p-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-100 disabled:bg-gray-100 disabled:text-[#9DA0A1]",
              className,
            )}
          >
            <span>{t("invoice.invoice_locked.am_contact.write_email")}</span>

            <MailIcon className="size-3.5" />
          </a>
          <AmContactCopyButton email={contact.email} from={from} />
        </div>
      </div>
    </div>
  );
};

type AmContactImageProps = {
  contact: ChooseContact;
};

export const AmContactImage: React.FC<AmContactImageProps> = ({ contact }) => {
  const { t } = useTranslation();

  return (
    <Avatar className="size-14">
      <AvatarImage
        src={
          contact.avatar
            ? `https://public.choose.app/avatars/80x80/${contact.avatar}-80x80.png`
            : undefined
        }
        srcSet={`https://public.choose.app/avatars/80x80/${contact.avatar}-80x80@2x.png 2x,
       https://public.choose.app/avatars/80x80/${contact.avatar}-80x80@3x.png 3x`}
        alt={t("invoice.invoice_locked.am_contact.profile_picture", {
          name: `${contact.firstName} ${contact.lastName}`,
        })}
      />
      <AvatarFallback>
        {contact.firstName.charAt(0).toUpperCase()}
        {contact.lastName.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
};

type AmContactCopyButtonProps = {
  className?: string;
  email: string;
  from: string;
};

export const AmContactCopyButton: React.FC<AmContactCopyButtonProps> = ({
  email,
  from,
}) => {
  const { t } = useTranslation();
  const [emailClipboardState, copyEmailToClipboard] = useCopyToClipboard();

  useEffect(() => {
    const { value, error } = emailClipboardState;
    if (value) {
      toast.success(t("clipboard.email_copied"));
    }
    if (error) {
      Sentry.captureException(error);
    }
  }, [emailClipboardState, t]);

  return (
    <Button
      type="button"
      onClick={() => {
        copyEmailToClipboard(email);
        track("CopyToClipboard", {
          label: "am_contact_email",
          from: from,
        });
      }}
    >
      <span>{t("invoice.invoice_locked.am_contact.copy_email")}</span>

      <CopyIcon className="size-3.5" />
    </Button>
  );
};
